/**
 * Grouped Patient Edit Screen (PRD.155).
 *
 * Renders a filtered view of the designated registration form for a specific
 * domain group (e.g. demographics, contact, medication). Updates are routed
 * through the existing save orchestrator — no second schema or persistence path.
 *
 * Route: /patient/:id/edit/:group
 */
import { useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { NavIcon, useBrandTokens } from '../../shell/shared';
import { usePowerSyncDb, usePowerSyncQuery } from '../../adapters/powersync';
import {
  qActiveFormByPurpose,
  qPatientById,
  qPatientContact,
  parseFieldsSchemaV1,
  flattenFields,
  filterSchemaByDomainGroups,
  type FormDefinitionRow,
  type FormFieldsSchemaV1,
  type DomainGroup,
  type PatientDetailRow,
  type PatientContactRow,
  orchestrateTwoWaySave,
  buildUpdateSql,
  buildInsertSql,
} from '@helios/data-access';
import { useAuth } from '@helios/auth-firebase-web';
import { SectionRenderer } from './encounters/FormFieldRenderers';

// ─── Helpers ───

function genId(): string {
  return crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

// ─── Group metadata ───

const GROUP_LABELS: Record<string, string> = {
  demographics: 'Demographics',
  contact: 'Contact Details',
  medication: 'Medication',
  allergies: 'Allergies',
  notes_alerts: 'Notes & Alerts',
  identifiers: 'Identifiers',
  emergency_contact: 'Emergency Contact',
};

// ─── Binding data builder ───

/**
 * Build the binding data map from patient and contact rows for all known
 * patient-source fields. This allows the renderer to show current values
 * for bound fields that the user can then edit (two_way) or see (read_only).
 */
function buildPatientBindingData(
  patient: PatientDetailRow | null,
  contact: PatientContactRow | null,
): Record<string, string> {
  const map: Record<string, string> = {};
  if (patient) {
    const simple: (keyof PatientDetailRow)[] = [
      'full_name',
      'birth_date',
      'sex',
      'patient_number',
      'blood_group_abo',
      'blood_group_rh',
      'drug_allergies_notes',
      'food_allergies_notes',
      'medical_notes',
      'medical_alerts',
      'medical_history',
      'past_medication_notes',
      'current_medication_notes',
      'title',
      'local_name',
      'marital_status',
      'nationality',
      'country_of_birth',
      'race',
      'religion',
      'occupation',
      'assigned_doctor',
    ];
    for (const key of simple) {
      const val = patient[key];
      if (val) map[`patient.${key}`] = val;
    }
  }
  if (contact) {
    const contactKeys: (keyof PatientContactRow)[] = [
      'email',
      'mobile_number',
      'formal_telephone',
      'formal_residential_address',
      'formal_province_state',
      'formal_district',
      'formal_postal_code',
    ];
    for (const key of contactKeys) {
      const val = contact[key];
      if (val) map[`patient.${key}`] = val;
    }
    // mobile_number with country code prefix
    if (contact.mobile_number) {
      const code = contact.mobile_country_code ? `${contact.mobile_country_code} ` : '';
      map['patient.mobile_number'] = `${code}${contact.mobile_number}`;
    }
  }
  map['form_context.current_date'] = new Date().toISOString().slice(0, 10);
  return map;
}

// ─── Page Component ───

export function PatientGroupEditPage() {
  const { id: patientId, group: groupParam } = useParams<{ id: string; group: string }>();
  const navigate = useNavigate();
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';

  const groups = useMemo<DomainGroup[]>(() => {
    if (!groupParam) return [];
    return groupParam.split(',') as DomainGroup[];
  }, [groupParam]);
  const groupLabel = groups.map((g) => GROUP_LABELS[g] ?? g).join(', ');

  // ── Resolve designated registration form ──
  const { data: designatedRows = [] } = usePowerSyncQuery<FormDefinitionRow>(
    qActiveFormByPurpose(orgId, 'registration'),
  );
  const designatedForm = designatedRows[0] ?? null;

  const fullSchema = useMemo<FormFieldsSchemaV1>(() => {
    if (!designatedForm) return { version: 1, sections: [] };
    return parseFieldsSchemaV1(designatedForm.fields_schema);
  }, [designatedForm?.id, designatedForm?.fields_schema]);

  // ── Derive grouped schema ──
  const groupedSchema = useMemo(
    () => filterSchemaByDomainGroups(fullSchema, groups),
    [fullSchema, groups],
  );

  // ── Load patient data for binding resolution ──
  const { data: patientRows = [] } = usePowerSyncQuery<PatientDetailRow>(
    qPatientById(patientId ?? ''),
  );
  const patient = patientRows[0] ?? null;

  const { data: contactRows = [] } = usePowerSyncQuery<PatientContactRow>(
    qPatientContact(patientId ?? ''),
  );
  const contact = contactRows[0] ?? null;

  const bindingData = useMemo(() => buildPatientBindingData(patient, contact), [patient, contact]);

  // ── Local form state (pre-seeded from binding data on first render) ──
  const [values, setValues] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const handleValueChange = useCallback((fieldKey: string, val: string) => {
    setValues((prev) => ({ ...prev, [fieldKey]: val }));
  }, []);

  // ── Submit handler ──
  const handleSave = useCallback(async () => {
    if (!db || !patientId || isSaving) return;
    setIsSaving(true);
    setError(null);

    try {
      // Validate required fields in the grouped schema only
      const allFields = flattenFields(groupedSchema);
      const missing = allFields.filter((f) => f.required && !values[f.key]?.trim());
      if (missing.length > 0) {
        setError(
          `Please fill in required fields: ${missing.map((f) => f.label || f.key).join(', ')}`,
        );
        setIsSaving(false);
        return;
      }

      // Orchestrate — pass only the grouped schema and current values
      const result = orchestrateTwoWaySave(groupedSchema, values);

      for (const op of result.operations) {
        if (op.table === 'patients') {
          const { sql, params } = buildUpdateSql(op);
          await db.execute(`${sql} WHERE id = ?`, [...params, patientId]);
        } else if (op.table === 'patient_contacts') {
          if (contact?.id) {
            const { sql, params } = buildUpdateSql(op);
            await db.execute(`${sql} WHERE id = ?`, [...params, contact.id]);
          } else {
            const { sql, params } = buildInsertSql(op, {
              id: genId(),
              patient_id: patientId,
            });
            await db.execute(sql, params);
          }
        }
      }

      // Multi-row operations (e.g. patient_allergies)
      for (const mop of result.multiRowOps) {
        await db.execute(
          `UPDATE ${mop.table} SET deleted_at = datetime('now') WHERE patient_id = ? AND ${mop.filterColumn} = ? AND deleted_at IS NULL`,
          [patientId, mop.filterValue],
        );
        for (const val of mop.values) {
          const staticEntries = Object.entries(mop.staticColumns);
          const cols = [
            'id',
            'patient_id',
            mop.valueColumn,
            ...staticEntries.map(([k]) => k),
            'updated_at',
          ];
          const placeholders = cols.map((c) => (c === 'updated_at' ? "datetime('now')" : '?'));
          const params = [genId(), patientId, val, ...staticEntries.map(([, v]) => v)];
          await db.execute(
            `INSERT INTO ${mop.table} (${cols.join(', ')}) VALUES (${placeholders.join(', ')})`,
            params,
          );
        }
      }

      setSaved(true);
    } catch (err) {
      console.error('[PatientGroupEdit] Save failed:', err);
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setIsSaving(false);
    }
  }, [db, patientId, groupedSchema, values, isSaving, contact?.id]);

  // ── No designated form ──
  if (!designatedForm) {
    return (
      <div style={{ padding: 'var(--sp-5)', maxWidth: 600 }}>
        <div
          style={{
            padding: 'var(--sp-4)',
            background: 'var(--color-info-bg, #eff6ff)',
            border: '1px solid var(--color-info, #3b82f6)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--color-info, #1d4ed8)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          No registration form has been designated. To use grouped patient editing, assign a
          published form the &ldquo;Registration&rdquo; purpose in Settings &rarr; Forms.
        </div>
      </div>
    );
  }

  // ── No matching fields for this group ──
  if (groupedSchema.sections.length === 0) {
    return (
      <div style={{ padding: 'var(--sp-5)', maxWidth: 600 }}>
        <div
          style={{
            padding: 'var(--sp-4)',
            background: 'var(--color-warning-bg, #fefce8)',
            border: '1px solid var(--color-warning-text, #a16207)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--color-warning-text, #a16207)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          The registration form does not contain any fields in the &ldquo;{groupLabel}&rdquo; group.
          Add bound fields with this domain group in the form builder.
        </div>
      </div>
    );
  }

  // ── Saved confirmation ──
  if (saved) {
    return (
      <div
        style={{
          padding: 'var(--sp-5)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'var(--sp-3)',
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: 'var(--color-success-bg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <NavIcon name="check-circle" />
        </div>
        <p style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text)' }}>
          {groupLabel} updated successfully.
        </p>
        <button
          onClick={() => navigate(`/patient/${patientId}`)}
          style={{
            height: 36,
            padding: '0 20px',
            borderRadius: 'var(--radius-md)',
            border: 'none',
            background: primary,
            color: '#fff',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            cursor: 'pointer',
          }}
        >
          Back to Patient
        </button>
      </div>
    );
  }

  // ── Form rendering ──
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div
        style={{
          padding: 'var(--sp-3) var(--sp-5)',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
          <button
            onClick={() => navigate(`/patient/${patientId}`)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-muted)',
              padding: 4,
            }}
          >
            <NavIcon name="arrow-left" />
          </button>
          <h1
            style={{
              margin: 0,
              fontSize: 'var(--font-size-lg)',
              fontWeight: 'var(--font-weight-semi)',
            }}
          >
            Update {groupLabel}
          </h1>
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving}
          style={{
            height: 36,
            padding: '0 20px',
            borderRadius: 'var(--radius-md)',
            border: 'none',
            background: isSaving ? 'var(--text-muted)' : primary,
            color: '#fff',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            cursor: isSaving ? 'default' : 'pointer',
            opacity: isSaving ? 0.7 : 1,
          }}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>

      {/* Error banner */}
      {error && (
        <div
          style={{
            margin: 'var(--sp-4) var(--sp-5) 0',
            padding: 'var(--sp-3) var(--sp-4)',
            background: 'var(--color-danger-bg, #fef2f2)',
            border: '1px solid var(--color-danger-border, #fecaca)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--color-danger-text, #dc2626)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          {error}
        </div>
      )}

      {/* Grouped form sections */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <div style={{ padding: 'var(--sp-5)' }}>
          <div
            style={{
              maxWidth: 720,
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--sp-5)',
            }}
          >
            {groupedSchema.sections.map((section) => (
              <SectionRenderer
                key={section.id}
                section={section}
                values={values}
                onValueChange={handleValueChange}
                readOnly={false}
                bindingData={bindingData}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
